import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { useEffect, useState } from 'react'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { auth } from '../services/firebaseAuth'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../stores/auth.store'
import { getPostAuthRedirectPath } from '../services/userProfile'

export function RegisterForm({ className, ...props }: React.ComponentProps<'form'>) {
    const [email, setEmail] = useState('')
    const [password1, setPassword1] = useState('')
    const [password2, setPassword2] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const router = useNavigate()
    const { user, loading: loadingUser } = useAuth()
    useEffect(() => {
        if (!loadingUser && user) {
            router(getPostAuthRedirectPath(user), { replace: true })
        }
    }, [user, loadingUser, router])
    const handleSignup = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        // validation
        //

        if (password1 !== password2) {
            setError('password do not match')
            setLoading(false)
            return
        }
        if (password1.length < 6) {
            setError('Password must be at least 6 characters')
            setLoading(false)
            return
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid email address')
            setLoading(false)
            return
        }

        try {
            await createUserWithEmailAndPassword(auth, email, password1)
        } catch (error: any) {
            console.error('Firebase error', error)

            // user friendly error

            switch (error.code) {
                case 'auth/email-already-in-use':
                    setError('This email is already registered')
                    break
                case 'auth/invalid-email':
                    setError('Please enter a valid email address')
                    break
                case 'auth/weak-password':
                    setError('Password should be at least 6 characters')
                    break
                default:
                    setError('Failed to create account. Please try again.')
            }
        } finally {
            setLoading(false)
        }
        // console.log(auth) // Should show FirebaseAuth object
    }
    return (
        <form onSubmit={handleSignup} className={cn('flex flex-col gap-6', className)} {...props}>
            <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">Build Your Storyverse</h1>
                <p className="text-muted-foreground text-sm text-balance">
                    Enter your email below to register to your account
                </p>
            </div>
            {error && (
                <div className="text-red-500 text-sm text-center p-2 bg-red-50 rounded-md">
                    {error}
                </div>
            )}
            <div className="grid gap-6">
                <div className="grid gap-3">
                    <Label htmlFor="email">Email</Label>
                    <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>
                <div className="grid gap-3">
                    <div className="space-y-3">
                        <div className="flex items-center">
                            <Label htmlFor="password">Password</Label>
                        </div>
                        <Input
                            id="password1"
                            type="password"
                            placeholder="Enter your password"
                            value={password1}
                            onChange={(e) => setPassword1(e.target.value)}
                            required
                        />
                    </div>
                </div>
                <div className="grid gap-3">
                    <div className="space-y-3">
                        <div className="flex items-center">
                            <Label htmlFor="password">Confirm Password</Label>
                        </div>
                        <Input
                            id="password2"
                            type="password"
                            placeholder="Re enter your password"
                            value={password2}
                            onChange={(e) => setPassword2(e.target.value)}
                            required
                        />
                    </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'creating account....' : 'Register'}
                </Button>
            </div>
            <div className="text-center text-sm">
                Already have an account?{' '}
                <a href="/login" className="underline underline-offset-4">
                    Login
                </a>
            </div>
        </form>
    )
}
